import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { styles as S } from '../styles';
import { STUNDEN, TAGE, parseTime, formatTime, datumImWochentag } from '../utils';
import { IconRepeat, IconWarn } from '../icons';
const ROW = 48; // Pixel pro Stunde
const ERSTE = STUNDEN[0]; // erste angezeigte Stunde
const HOEHE = STUNDEN.length * ROW;
/**
 * Weist überlappenden Terminen eines Tages Spuren (lanes) zu, sodass sie
 * nebeneinander dargestellt werden (wie im Google Kalender).
 */
function layoutTag(evs) {
    const sortiert = [...evs].sort((a, b) => parseTime(a.start_zeit) - parseTime(b.start_zeit) ||
        parseTime(a.end_zeit) - parseTime(b.end_zeit));
    const ergebnis = [];
    let i = 0;
    while (i < sortiert.length) {
        // Cluster transitiv überlappender Termine bilden
        const cluster = [sortiert[i]];
        let maxEnde = parseTime(sortiert[i].end_zeit);
        let j = i + 1;
        while (j < sortiert.length && parseTime(sortiert[j].start_zeit) < maxEnde) {
            cluster.push(sortiert[j]);
            maxEnde = Math.max(maxEnde, parseTime(sortiert[j].end_zeit));
            j++;
        }
        // Spuren innerhalb des Clusters vergeben
        const spurEnde = [];
        const spurVon = new Map();
        for (const ev of cluster) {
            const s = parseTime(ev.start_zeit);
            const e = parseTime(ev.end_zeit);
            let platz = -1;
            for (let k = 0; k < spurEnde.length; k++) {
                if (s >= spurEnde[k]) {
                    spurEnde[k] = e;
                    platz = k;
                    break;
                }
            }
            if (platz < 0) {
                platz = spurEnde.length;
                spurEnde.push(e);
            }
            spurVon.set(ev, platz);
        }
        const spalten = spurEnde.length;
        for (const ev of cluster) {
            ergebnis.push({ b: ev, lane: spurVon.get(ev) ?? 0, spalten });
        }
        i = j;
    }
    return ergebnis;
}
export function WocheGesamt({ belegungen, sperren, platzById, teamById, konflikte, onSelect, montag }) {
    return (_jsx("div", { style: S.rasterWrap, children: _jsxs("div", { style: S.gesamtGrid, children: [_jsx("div", { style: S.gesamtCorner }), TAGE.map((t, i) => {
                    const d = datumImWochentag(montag, i + 1);
                    return (_jsxs("div", { style: S.tagHead, children: [_jsx("span", { style: S.tagKurz, children: t }), _jsxs("span", { style: S.tagDatum, children: [d.getDate(), ".", d.getMonth() + 1, "."] })] }, t));
                }), _jsx("div", { style: S.gesamtZeitCol, children: STUNDEN.map((std) => (_jsxs("div", { style: { ...S.gesamtZeitLabel, height: ROW }, children: [String(std).padStart(2, '0'), ":00"] }, std))) }), TAGE.map((_, tagIdx) => {
                    const tag = tagIdx + 1;
                    const tagEvents = belegungen.filter((b) => b.wochentag === tag);
                    const platziert = layoutTag(tagEvents);
                    return (_jsxs("div", { style: { ...S.gesamtDay, height: HOEHE }, children: [sperren.filter((s) => s.wochentag === tag).map((s) => {
                                const sVon = s.ganztags ? STUNDEN[0] : parseTime(s.start_zeit ?? '00:00');
                                const sBis = s.ganztags ? STUNDEN[STUNDEN.length - 1] + 1 : parseTime(s.end_zeit ?? '23:59');
                                const top = Math.max(0, sVon - ERSTE) * ROW;
                                const hoehe = Math.max(14, (sBis - sVon) * ROW - 2);
                                const platz = platzById(s.platz_id);
                                return (_jsx("div", { style: { ...S.gesamtSperre, top, height: hoehe }, title: `${platz?.name ?? ''}: ${s.grund || 'Gesperrt'}`, children: _jsxs("div", { style: S.gesamtSperreText, children: ["\uD83D\uDD12 ", s.grund || 'Gesperrt'] }) }, `sperre-${s.id}`));
                            }), platziert.map(({ b, lane, spalten }) => {
                                const von = parseTime(b.start_zeit);
                                const bis = parseTime(b.end_zeit);
                                const top = Math.max(0, von - ERSTE) * ROW;
                                const hoehe = Math.max(16, (bis - von) * ROW - 2);
                                const platz = platzById(b.platz_id);
                                const team = teamById(b.team_id);
                                const farbe = platz?.farbe ?? '#888';
                                const konflikt = konflikte.has(b.id);
                                const entfaellt = b._status === 'entfaellt';
                                const geaendert = b._status === 'geaendert';
                                const angefragt = b.status === 'angefragt';
                                const breite = 100 / spalten;
                                return (_jsxs("div", { role: "button", tabIndex: 0, onClick: () => onSelect(b), onKeyDown: (e) => e.key === 'Enter' && onSelect(b), style: {
                                        ...S.gesamtEvent,
                                        top,
                                        height: hoehe,
                                        left: `calc(${lane * breite}% + 1px)`,
                                        width: `calc(${breite}% - 2px)`,
                                        background: entfaellt ? '#f4f5f3' : angefragt ? '#fef9e7' : `${farbe}1f`,
                                        borderLeft: `3px solid ${entfaellt ? '#bcc2b8' : konflikt ? '#dc2626' : angefragt ? '#d4a017' : farbe}`,
                                        border: angefragt ? '1px dashed #d4a017' : undefined,
                                        borderLeftWidth: 3,
                                        opacity: entfaellt ? 0.7 : 1,
                                    }, children: [_jsxs("div", { style: S.gesamtEventKopf, children: [_jsx("strong", { style: {
                                                        ...S.gesamtEventTeam,
                                                        textDecoration: entfaellt ? 'line-through' : 'none',
                                                    }, children: team?.name ?? b.titel ?? '—' }), b.wiederkehrend && !entfaellt && _jsx(IconRepeat, { size: 10, style: { opacity: 0.5, flexShrink: 0 } }), geaendert && _jsx("span", { style: S.geaendertDot }), angefragt && _jsx("span", { style: S.angefragtPunkt, title: "Angefragt" }), konflikt && !entfaellt && _jsx(IconWarn, { size: 10, style: { color: '#dc2626', flexShrink: 0 } })] }), _jsx("div", { style: S.gesamtEventMeta, children: entfaellt ? 'Entfällt' : angefragt ? `${formatTime(von)}–${formatTime(bis)} · angefragt` : `${formatTime(von)}–${formatTime(bis)}` }), hoehe > 44 && platz && (_jsxs("div", { style: S.gesamtEventPlatz, children: [_jsx("span", { style: { ...S.platzDot, background: farbe, width: 7, height: 7 } }), platz.name] }))] }, `${b.id}-${lane}`));
                            })] }, tag));
                })] }) }));
}
